import { create } from "zustand";
import type { AudioState } from "../domain/index.js";
import {
  type HistoryBranch,
  type HistoryNode,
  getHistory,
  getStateAt,
  createBranch as apiCreateBranch,
} from "../infra/api.js";

/**
 * History-tree store (spec §6.3 / §7.2). It is a separate store from
 * `compositionStore` on purpose: the tree is a pointed read fetched out of the
 * SSE event stream, and time travel materializes a *past* `AudioState` that
 * must never be confused with the live present.
 *
 * Time travel is strictly READ-ONLY — `getStateAt` never appends an event;
 * `travelState` is a throwaway projection used only to render the past.
 */
export interface HistoryStoreState {
  projectId: string | null;
  nodes: HistoryNode[];
  branches: HistoryBranch[];
  loading: boolean;
  error: string | null;
  /** The event currently being viewed in time-travel mode; `null` = present. */
  travelEventId: string | null;
  /** The materialized past state for `travelEventId`; `null` when not traveling. */
  travelState: AudioState | null;
  travelLoading: boolean;
  travelError: string | null;
  branchStatus: "idle" | "creating" | "error";
  branchError: string | null;
  /** Fetches the history projection for a project. */
  load(projectId: string): Promise<void>;
  /** Enters time-travel mode at `eventId` — materializes the past, read-only. */
  travelTo(eventId: string): Promise<void>;
  /** Leaves time-travel mode and returns to the live present. */
  returnToPresent(): void;
  /** Marks a branch off `fromEventId`; refreshes the projection on success. */
  branch(fromEventId: string, label: string): Promise<void>;
  /** Clears every history state — call on editor unmount. */
  reset(): void;
}

export const useHistoryStore = create<HistoryStoreState>((set, get) => ({
  projectId: null,
  nodes: [],
  branches: [],
  loading: false,
  error: null,
  travelEventId: null,
  travelState: null,
  travelLoading: false,
  travelError: null,
  branchStatus: "idle",
  branchError: null,

  async load(projectId) {
    set({ projectId, loading: true, error: null });
    try {
      const { nodes, branches } = await getHistory(projectId);
      // Drop a stale response if the editor moved to another project.
      if (get().projectId !== projectId) return;
      set({ nodes, branches, loading: false });
    } catch (e) {
      if (get().projectId !== projectId) return;
      set({ error: e instanceof Error ? e.message : String(e), loading: false });
    }
  },

  async travelTo(eventId) {
    const projectId = get().projectId;
    if (!projectId) return;
    set({ travelEventId: eventId, travelLoading: true, travelError: null });
    try {
      const { state } = await getStateAt(projectId, eventId);
      // Ignore a response that lost the race with a later node click / return.
      if (get().travelEventId !== eventId) return;
      set({ travelState: state, travelLoading: false });
    } catch (e) {
      if (get().travelEventId !== eventId) return;
      set({
        travelError: e instanceof Error ? e.message : String(e),
        travelLoading: false,
      });
    }
  },

  returnToPresent() {
    set({ travelEventId: null, travelState: null, travelLoading: false, travelError: null });
  },

  async branch(fromEventId, label) {
    const projectId = get().projectId;
    if (!projectId) throw new Error("no project");
    set({ branchStatus: "creating", branchError: null });
    try {
      await apiCreateBranch(projectId, fromEventId, label);
      set({ branchStatus: "idle" });
      // The new HistoryBranchCreated event is in the log — refresh the tree.
      await get().load(projectId);
    } catch (e) {
      const error = e instanceof Error ? e.message : String(e);
      set({ branchStatus: "error", branchError: error });
      throw e;
    }
  },

  reset() {
    set({
      projectId: null,
      nodes: [],
      branches: [],
      loading: false,
      error: null,
      travelEventId: null,
      travelState: null,
      travelLoading: false,
      travelError: null,
      branchStatus: "idle",
      branchError: null,
    });
  },
}));
